import React from 'react';
import { StandardTableActions } from '../../../components/ui';

import { Column } from '../../../components/DataTable/DataTable';
import { Priority } from './useData';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';

export const moduleConfig = {
  module: {
    title: 'Priorities',
    description: 'Manage priorities',
    permissionModuleKey: 'priority',
    route: {
      list: '/priority',
      new: '/priority/new',
      detail: (id: number) => `/priority/${id}`,
      edit: (id: number) => `/priority/${id}/edit`,
    },
    api: {
      list: '/p-crm-priority',
      detail: (id: number) => `/p-crm-priority/${id}`,
    },
  },
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },
  search: {
    placeholder: 'Search priorities...',
  },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: ['p_descr'],
    },
  ],
  filters: {
    fields: [
      {
        key: 'p_descr',
        label: 'Description',
        placeholder: 'Type to filter description...',
        type: 'text' as const,
        getValue: (item: Priority) => item.p_descr,
        options: [],
      },
    ],
    applyFilter: (items: Priority[], filters: { p_descr: string[] | string }) => {
      return items.filter((item) => {
        const descrFilter = filters.p_descr;
        if (typeof descrFilter === 'string' && descrFilter.trim() !== '') {
          const value = (item.p_descr || '').toLowerCase();
          if (!value.includes(descrFilter.toLowerCase())) return false;
        } else if (Array.isArray(descrFilter) && descrFilter.length > 0) {
          const value = (item.p_descr || '').toLowerCase();
          const matches = descrFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }
        return true;
      });
    },
  },
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Prioritie',
        showTitle: false,
        variant: 'primary' as const,
        href: '/priority/new',
      },
    ],
  },
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: Priority) => {
      window.location.href = `/priority/${item.id}`;
    },
    keyExtractor: (item: Priority) => item.id,
  },
  emptyStates: {
    noData: 'Get started by creating a new prioritie.',
    noSearchResults: 'No priorities found matching your search criteria.',
    noFilterResults: 'No priorities match the selected filters.',
  },
  delete: {
    title: 'Delete Prioritie',
    message: 'Are you sure you want to delete this prioritie? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete prioritie. Please try again.',
    handler: async (id: number) => {
      const { deletePriority } = await import('../../../services/mutations');
      await deletePriority(id);
    },
  },
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },
  detail: {
    subtitle: 'Prioritie Details',
    notFoundTitle: 'Prioritie not found',
    notFoundMessage: 'The prioritie you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Priorities',
    defaultTitle: 'Unnamed Prioritie',
    getTitle: (item: Priority) => {
      return item.p_descr || `Prioritie #${item.id}`;
    },
  },
  form: {
    new: {
      title: 'New Prioritie',
      subtitle: 'Add a new prioritie',
      submitText: 'Create Prioritie',
    },
    edit: {
      title: 'Edit Prioritie',
      subtitle: 'Update prioritie information',
      submitText: 'Update Prioritie',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading prioritie...',
    errorMessage: 'Failed to save prioritie. Please try again.',
    create: async (data: Partial<Priority>) => {
      const { createPriority } = await import('../../../services/mutations');
      return await createPriority(data);
    },
    update: async (id: number, data: Partial<Priority>) => {
      const { updatePriority } = await import('../../../services/mutations');
      return await updatePriority(id, data);
    },
  },
  fields: [
    {
      name: 'p_descr',
      label: 'Description',
      type: 'text',
      required: true,
      placeholder: 'Enter prioritie description',
      validation: {
        required: true,
        minLength: 1,
        maxLength: 255,
      },
    },
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Prioritie Information',
        icon: '📝',
        fields: ['p_descr'],
        columns: 1,
        collapsible: false,
      },
    ],
  } as FormLayout,
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<Priority>[] => [
  {
    key: 'id',
    header: 'ID',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-900">{value || '-'}</span>
    ),
  },
  {
    key: 'p_descr',
    header: 'Description',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-900">{value || '-'}</span>
    ),
  },
];

export const getDetailSections = (
  item: Priority,
  formatDate: (dateString?: string) => string
) => {
  return [
    {
      title: 'Basic Information',
      icon: '📝',
      fields: [
        {
          label: 'Description',
          value: item.p_descr || '-',
        },
      ],
    },
  ];
};

export const getStatusBadges = (item: Priority) => {
  return null;
};

export const getInitialFormData = (): Partial<Priority> => {
  return {
    p_descr: '',
  };
};

export const mapItemToFormData = (item: Priority): Partial<Priority> => {
  return {
    p_descr: item.p_descr || '',
  };
};

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    default:
      return null;
  }
};

export const getTableActions = (
  item: Priority,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.id)}
      editTo={moduleConfig.module.route.edit(item.id)}
      onDelete={() => onDelete(item.id)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};

